import { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { Check } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { Button } from '../common/Button';
import { useDefensePlansStore, unpublishedAssignmentUpdates } from '../../store/defensePlansStore';
import { useUIStore } from '../../store/uiStore';
import { getErrorMessage } from '../../utils/errorHandler';
import { DEFENSE_STRATEGIES } from './defenseStrategies';
import { partyDisplayName } from './planParties';
import { ASSIGNMENT_STATUS_LABEL } from '../../utils/planAssignmentSummary';
import type { DefensePlanFeature, PlanAssignmentUpdate, PlanParty } from '../../types';

// Owner-only: reported progress that hasn't gone out as News yet, reached
// from DefensePlanPanel's "New updates (n)" affordance. A row per report —
// who, which defense, the new status, their note — with a checkbox that
// starts on for 'done'/'accepted' and off for 'declined' (a decline is a
// staffing problem, not news). Publishing rolls the checked rows into ONE
// post (publishPlanUpdateNews); a successful post then offers one more
// step, emailing it straight to the plan's parties (sendCommunityUpdate).

const STATUS_VERB: Record<string, string> = {
  done: 'finished',
  accepted: 'took on',
  declined: 'passed on',
};

// `lowercaseStrategy` matches featureDisplayName in
// functions/src/plans/defenseStrategyLabels.ts: mid-sentence the strategy is
// lower case and the street address is left as the owner typed it.
function featureLabel(
  feature: DefensePlanFeature | undefined,
  options?: { lowercaseStrategy?: boolean }
): string {
  if (!feature) return 'a defense on this plan';
  const label = DEFENSE_STRATEGIES[feature.strategy].label;
  const base = options?.lowercaseStrategy ? label.toLowerCase() : label;
  return feature.address ? `${base} at ${feature.address}` : base;
}

function partyLabel(party: PlanParty | undefined): string {
  return party ? partyDisplayName(party) : 'A group on this plan';
}

// Close enough to publishPlanUpdateNews.ts's own generated title to preview
// it, not a byte-for-byte match: this is only a suggestion, and the server
// generates its own real one whenever the field below is left untouched
// (the `titleTouched` state further down).
function suggestTitle(
  selected: PlanAssignmentUpdate[],
  parties: Record<string, PlanParty>,
  featuresById: Record<string, DefensePlanFeature>,
  planTitle: string
): string {
  if (selected.length === 0) return '';
  if (selected.length === 1) {
    const update = selected[0];
    const verb = STATUS_VERB[update.status] ?? 'updated';
    const what = featureLabel(featuresById[update.featureId], { lowercaseStrategy: true });
    return `${partyLabel(parties[update.partyId])} ${verb} ${what}`;
  }
  return `${selected.length} updates on ${planTitle}`;
}

function UpdateRow({
  update,
  party,
  feature,
  checked,
  onToggle,
}: {
  update: PlanAssignmentUpdate;
  party: PlanParty | undefined;
  feature: DefensePlanFeature | undefined;
  checked: boolean;
  onToggle: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onToggle}
      testID={`plan-updates-row-${update.id}`}
      accessibilityRole="checkbox"
      aria-checked={checked}
      accessibilityLabel={`${partyLabel(party)}, ${featureLabel(feature)}, ${ASSIGNMENT_STATUS_LABEL[update.status]}`}
      {...hoverProps}
      style={[
        styles.row,
        CURSOR_POINTER,
        {
          borderColor: checked ? colors.borderFocus : colors.border,
          backgroundColor: checked || hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <View
        style={[
          styles.checkbox,
          {
            borderColor: checked ? colors.action : colors.border,
            backgroundColor: checked ? colors.action : 'transparent',
          },
        ]}>
        {checked && <Check size={12} color={colors.actionOn} />}
      </View>
      <View style={styles.rowText}>
        <Text style={[styles.rowTitle, { color: colors.text }]} numberOfLines={1}>
          {partyLabel(party)} · {ASSIGNMENT_STATUS_LABEL[update.status]}
        </Text>
        <Text style={[styles.rowMeta, { color: colors.textSecondary }]} numberOfLines={1}>
          {featureLabel(feature)}
        </Text>
        {update.note && (
          <Text style={[styles.rowNote, { color: colors.textTertiary }]} numberOfLines={2}>
            &ldquo;{update.note}&rdquo;
          </Text>
        )}
      </View>
    </Pressable>
  );
}

export function PlanUpdatesView({
  planId,
  onClose,
  hideTitle = false,
}: {
  planId: string | null;
  onClose: () => void;
  /** The desktop companion's header already names this view and its
   *  object ("New updates · Pine Knot plan"), so the form's own
   *  title would repeat it. The phone sheet has no header and leaves it on. */
  hideTitle?: boolean;
}) {
  const { colors } = useTheme();
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  const assignmentUpdatesByPlan = useDefensePlansStore((s) => s.assignmentUpdatesByPlan);
  const partiesByPlan = useDefensePlansStore((s) => s.partiesByPlan);
  const publishPlanUpdateNews = useDefensePlansStore((s) => s.publishPlanUpdateNews);
  const sendCommunityUpdate = useDefensePlansStore((s) => s.sendCommunityUpdate);

  const parties = planId ? (partiesByPlan[planId] ?? {}) : {};
  const featuresById = useMemo(
    () => Object.fromEntries((ownPlan?.features ?? []).map((f) => [f.id, f])),
    [ownPlan?.features]
  );

  const updates = useMemo(() => {
    const map = planId ? (assignmentUpdatesByPlan[planId] ?? {}) : {};
    return unpublishedAssignmentUpdates(map).sort(
      (a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()
    );
  }, [planId, assignmentUpdatesByPlan]);
  // A stable key of ids (not `updates` itself, a fresh array every
  // snapshot) — the selection-default effect below should only re-run when
  // the set of rows actually changes, not on every unrelated re-subscribe.
  const updateIdsKey = updates.map((u) => u.id).join(',');

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setSelected((prev) => {
      const next: Record<string, boolean> = {};
      for (const update of updates) {
        next[update.id] = prev[update.id] ?? update.status !== 'declined';
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateIdsKey]);

  const selectedUpdates = updates.filter((u) => selected[u.id]);

  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const suggestedTitle = suggestTitle(
    selectedUpdates,
    parties,
    featuresById,
    ownPlan?.title ?? 'this plan'
  );
  useEffect(() => {
    if (!titleTouched) setTitle(suggestedTitle);
  }, [suggestedTitle, titleTouched]);

  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [posted, setPosted] = useState<{ postId: string; published: number } | null>(null);
  const [emailing, setEmailing] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailResult, setEmailResult] = useState<{ parties: number; suppressed: number } | null>(
    null
  );

  const handlePublish = async () => {
    if (!planId || selectedUpdates.length === 0) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const result = await publishPlanUpdateNews(
        planId,
        selectedUpdates.map((u) => u.id),
        // Only sent when the owner actually changed the field — an
        // untouched (or cleared-back-to-blank) field lets the server
        // generate its own headline.
        titleTouched ? title.trim() || undefined : undefined
      );
      setPosted({ postId: result.postId, published: result.published });
      useUIStore.getState().showToast({ message: 'Posted to News' });
    } catch (err) {
      setPublishError(getErrorMessage(err));
    } finally {
      setPublishing(false);
    }
  };

  const handleEmail = async () => {
    if (!planId || !posted) return;
    setEmailing(true);
    setEmailError(null);
    try {
      const result = await sendCommunityUpdate(planId, posted.postId);
      setEmailResult({ parties: result.parties, suppressed: result.suppressed });
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setEmailing(false);
    }
  };

  return (
    <View style={[styles.content, hideTitle && styles.contentCompanion]}>
      {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>New updates</Text>}

      {posted ? (
        <>
          <Text style={[styles.postedText, { color: colors.text }]} testID="plan-updates-posted">
            Posted {posted.published} update{posted.published === 1 ? '' : 's'} to News.
          </Text>
          {!emailResult ? (
            <Button
              title="Email this to the community"
              onPress={() => void handleEmail()}
              variant="primary"
              loading={emailing}
              testID="plan-updates-email"
              fullWidth
            />
          ) : (
            <Text style={[styles.postedText, { color: colors.textSecondary }]}>
              Sent to {emailResult.parties} {emailResult.parties === 1 ? 'party' : 'parties'}
              {emailResult.suppressed > 0 ? ` (${emailResult.suppressed} opted out)` : ''}.
            </Text>
          )}
          {emailError && <Text style={[styles.error, { color: colors.danger }]}>{emailError}</Text>}
          <Button
            title="Done"
            onPress={onClose}
            variant="secondary"
            testID="plan-updates-done"
            fullWidth
          />
        </>
      ) : updates.length === 0 ? (
        <Text style={[styles.emptyState, { color: colors.textTertiary }]}>
          Nothing waiting to post.
        </Text>
      ) : (
        <>
          <View style={styles.list}>
            {updates.map((update) => (
              <UpdateRow
                key={update.id}
                update={update}
                party={parties[update.partyId]}
                feature={featuresById[update.featureId]}
                checked={!!selected[update.id]}
                onToggle={() => setSelected((prev) => ({ ...prev, [update.id]: !prev[update.id] }))}
              />
            ))}
          </View>

          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Headline</Text>
          <TextInput
            value={title}
            onChangeText={(text) => {
              setTitle(text);
              setTitleTouched(true);
            }}
            placeholder={suggestedTitle || 'e.g. North fence thinning is done'}
            placeholderTextColor={colors.textTertiary}
            maxLength={120}
            testID="plan-updates-title"
            accessibilityLabel="Headline"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />

          {publishError && (
            <Text style={[styles.error, { color: colors.danger }]}>{publishError}</Text>
          )}

          <Button
            title="Post to News"
            onPress={() => void handlePublish()}
            variant="primary"
            disabled={selectedUpdates.length === 0}
            loading={publishing}
            testID="plan-updates-publish"
            fullWidth
          />
        </>
      )}

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="plan-updates-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.sm,
  },
  // In the desktop companion: the header's side padding, so the body shares
  // its left edge (the phone sheet keeps the sheet padding above).
  contentCompanion: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  list: {
    gap: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 2,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
    gap: 1,
  },
  rowTitle: {
    ...TYPOGRAPHY.label,
  },
  rowMeta: {
    ...TYPOGRAPHY.caption,
  },
  rowNote: {
    ...TYPOGRAPHY.caption,
    fontStyle: 'italic',
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
  },
  input: {
    ...TYPOGRAPHY.body,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  error: {
    ...TYPOGRAPHY.caption,
  },
  emptyState: {
    ...TYPOGRAPHY.caption,
  },
  postedText: {
    ...TYPOGRAPHY.body,
  },
  scrollSentinel: {
    height: 1,
  },
});
